import React, { useState, useEffect } from "react";
import axios from "axios";
import { BiFile, BiDownload } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls"; // Adjust path as needed
import apiClient from '../utils/apiClient.js';
import { useOnboarding } from '../Onboarding/OnboardingContext';

const AffidavitListComponent = ({ caseId, affidavitUpdateTrigger }) => {
    const [affidavits, setAffidavits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    let serveeId= null;

    const token = localStorage.getItem("token");

    const { runTour, stepIndex, setStepIndex } = useOnboarding();

    // AI: AUTO-FILL DUMMY AFFIDAVIT FOR TOUR
    useEffect(() => {
        if (runTour && stepIndex >= 10 && affidavits.length === 0) {
            setAffidavits([{
                id: 'demo-aff',
                servee_name: 'John Doe (DEMO)',
                created_at: new Date().toISOString(),
                download_url: '#',
                affidavit_file_name: 'Demo_Affidavit.pdf'
            }]);
            setLoading(false);
        }
    }, [runTour, stepIndex, affidavits.length]);

    useEffect(() => {
        const fetchAffidavits = async () => {
            if (runTour && stepIndex >= 10) {
                setAffidavits([{
                    id: 9999,
                    servee_name: "John Smith (Demo)",
                    created_at: new Date().toISOString(),
                    affidavit_file_name: "demo_affidavit.pdf",
                    download_url: "#"
                }]);
                setLoading(false);
                return;
            }
            if (!caseId) return;

            setLoading(true);
            setError(null);

            let url = `${API_BASE_URL}/servers/case/${caseId}/affidavits`;
            if (serveeId) {
                // Add serveeId as a query parameter if provided
                url += `?servee_id=${serveeId}`;
            }

            try {
                const res = await axios.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAffidavits(res.data.affidavits);

                if (res.data.affidavits && res.data.affidavits.length > 0) {
                    // TOUR FLOW 5: Wait 600ms for the Affidavit to appear on screen, then highlight Download button!
                    if (runTour && (stepIndex === 8 || stepIndex === 9)) {
                        setTimeout(() => setStepIndex(10), 600);
                    }
                }
                
            } catch (err) {
                console.error("Error fetching affidavits:", err);
                setError("Failed to load affidavits.");
            } finally {
                setLoading(false);
            }
        };

        fetchAffidavits();
    }, [caseId, serveeId, token]);

    if (loading) return <div className="text-center p-4">Loading affidavits...</div>;
    if (error) return <div className="text-center p-4 text-red-600">{error}</div>;
    
    // AI: TOUR DOWNLOAD HANDLER
    const handleTourDownload = (e, aff) => {
        if (runTour && aff.id === 'demo-aff') {
            e.preventDefault();
            
            try {
                const { jsPDF } = require("jspdf");
                const doc = new jsPDF({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'letter'
                });

                // --- Layout Settings ---
                const margin = 20;
                let yPos = 30;

                // --- Header ---
                doc.setFont("times", "bold");
                doc.setFontSize(20);
                doc.text("AFFIDAVIT OF SERVICE", 105, yPos, { align: "center" });
                
                doc.setLineWidth(0.5);
                doc.line(70, yPos + 2, 140, yPos + 2); // Underline

                yPos += 20;

                // --- Case Information ---
                doc.setFont("times", "normal");
                doc.setFontSize(12);
                doc.text(`Case No.: DEMO-56468`, margin, yPos);
                yPos += 7;
                doc.text(`Case Name: Doe vs. Smith - Subpoena`, margin, yPos);
                yPos += 7;
                doc.text(`Jurisdiction: District Court of Onboarding`, margin, yPos);
                yPos += 15;

                // --- Declaration ---
                doc.setFont("times", "italic");
                doc.text("I, Mahatma Gandi (Admin), being duly sworn, depose and state as follows:", margin, yPos);
                yPos += 12;

                doc.setFont("times", "normal");
                const p1 = "1. I am over the age of eighteen (18) years and not a party to the action referenced. I am a registered Process Server in this jurisdiction.";
                const p1Lines = doc.splitTextToSize(p1, 170);
                doc.text(p1Lines, margin, yPos);
                yPos += (p1Lines.length * 7);

                const p2 = `2. On ${new Date().toLocaleDateString()} at approximately ${new Date().toLocaleTimeString()}, I successfully served the following document(s): Summons and Complaint.`;
                const p2Lines = doc.splitTextToSize(p2, 170);
                doc.text(p2Lines, margin, yPos);
                yPos += (p2Lines.length * 7);

                const p3 = `3. Service was made upon John Smith at the address: 123 Onboarding Way, Demo City, ST 12345.`;
                const p3Lines = doc.splitTextToSize(p3, 170);
                doc.text(p3Lines, margin, yPos);
                yPos += (p3Lines.length * 15);

                // --- Signature Block ---
                doc.setFont("times", "bold");
                doc.text("VERIFICATION:", margin, yPos);
                yPos += 10;
                doc.setFont("times", "normal");
                doc.text("Signature of Server: __________________________", margin, yPos);
                yPos += 10;
                doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPos);

                // --- Footer ---
                doc.setFontSize(10);
                doc.setTextColor(150);
                doc.text(`Document generated via IntelServe Onboarding Tour | ID: demo-aff`, 105, 260, { align: "center" });

                // --- Save ---
                doc.save(aff.affidavit_file_name || 'Demo_Affidavit.pdf');

            } catch (err) {
                console.error("PDF Generation error:", err);
                // Fallback to basic blob if jspdf fails for any reason
                const blob = new Blob(["Demo Affidavit Content for IntelServe Onboarding"], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', aff.affidavit_file_name || 'Demo_Affidavit.pdf');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            }

            // Advance the tour
            if (stepIndex === 10) {
                apiClient.post('/onboarding/events', { event_name: 'first_report_generated' }).catch(() => {});
                setStepIndex(11);
            }
        }
    };

    // SCROLL FIX: Added tour-affidavits-container here
    if (affidavits.length === 0) return <div className="text-center p-4 text-gray-500 tour-affidavits-container">No affidavits have been generated for this {serveeId ? 'servee' : 'case'} yet.</div>;

    // SCROLL FIX: Added tour-affidavits-container here
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-md mt-6 mb-6 p-4 tour-affidavits-container">
            <h3 className="text-xl font-extrabold text-gray-900 mb-4 flex items-center space-x-2">
                <BiFile className="w-6 h-6 text-indigo-600" />
                <span>Generated Affidavits</span>
            </h3>

            <ul className="space-y-3">
                {affidavits.map((aff) => (
                    <li key={aff.id} className="p-3 bg-gray-50 border border-gray-200 rounded-md flex justify-between items-center">
                        <div>
                            <p className="text-sm font-semibold text-gray-800">
                                Servee: <span className="text-indigo-700">{aff.servee_name || 'N/A'}</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                Generated: {new Date(aff.created_at).toLocaleDateString()}
                            </p>
                        </div>
                        <div className="tour-create-affidavits">
                            <a 
                                href={aff.download_url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                onClick={(e) => { 
                                    if (runTour && aff.id === 'demo-aff') {
                                        handleTourDownload(e, aff);
                                    } else if (runTour && stepIndex === 10) {
                                        setStepIndex(11); 
                                    }
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-semibold flex items-center space-x-1 transition"
                            >
                                <BiDownload className="w-4 h-4" />
                                <span>Download ({aff.affidavit_file_name})</span>
                            </a>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AffidavitListComponent;
